import * as AlertDialogPrimitive from '@radix-ui/react-alert-dialog';
import * as React from 'react';

import { cn } from '@heidi/utils';

// Package-internal seam, deliberately not on the button door — see `../button/index.ts`.
import { useButtonProps } from '../button/use-button-props';

import {
  alertDialogCancelClasses,
  alertDialogContentClasses,
  alertDialogDescriptionClasses,
  alertDialogFooterClasses,
  alertDialogHeaderClasses,
  alertDialogOverlayClasses,
  alertDialogTitleClasses,
} from './alert-dialog.styles';
import type {
  AlertDialogActionProps,
  AlertDialogCancelProps,
  AlertDialogContentProps,
  AlertDialogDescriptionProps,
  AlertDialogFooterProps,
  AlertDialogHeaderProps,
  AlertDialogOverlayProps,
  AlertDialogTitleProps,
} from './alert-dialog.types';

const AlertDialog = AlertDialogPrimitive.Root;

const AlertDialogTrigger = AlertDialogPrimitive.Trigger;

const AlertDialogPortal = AlertDialogPrimitive.Portal;

const AlertDialogOverlay = React.forwardRef<
  React.ElementRef<typeof AlertDialogPrimitive.Overlay>,
  AlertDialogOverlayProps
>(({ className, showOverlay = true, ...props }, ref) => (
  <AlertDialogPrimitive.Overlay
    className={cn(alertDialogOverlayClasses, className, !showOverlay && 'bg-transparent')}
    {...props}
    ref={ref}
  />
));
AlertDialogOverlay.displayName = AlertDialogPrimitive.Overlay.displayName;

const AlertDialogContent = React.forwardRef<
  React.ElementRef<typeof AlertDialogPrimitive.Content>,
  AlertDialogContentProps
>(({ className, showOverlay = true, ...props }, ref) => (
  <AlertDialogPortal>
    <AlertDialogOverlay showOverlay={showOverlay} />
    <AlertDialogPrimitive.Content
      ref={ref}
      className={cn(alertDialogContentClasses, className)}
      {...props}
    />
  </AlertDialogPortal>
));
AlertDialogContent.displayName = AlertDialogPrimitive.Content.displayName;

const AlertDialogHeader = ({ className, ...props }: AlertDialogHeaderProps) => (
  <div className={cn(alertDialogHeaderClasses, className)} {...props} />
);
AlertDialogHeader.displayName = 'AlertDialogHeader';

const AlertDialogFooter = ({ className, ...props }: AlertDialogFooterProps) => (
  <div className={cn(alertDialogFooterClasses, className)} {...props} />
);
AlertDialogFooter.displayName = 'AlertDialogFooter';

const AlertDialogTitle = React.forwardRef<
  React.ElementRef<typeof AlertDialogPrimitive.Title>,
  AlertDialogTitleProps
>(({ className, ...props }, ref) => (
  <AlertDialogPrimitive.Title
    ref={ref}
    className={cn(alertDialogTitleClasses, className)}
    {...props}
  />
));
AlertDialogTitle.displayName = AlertDialogPrimitive.Title.displayName;

const AlertDialogDescription = React.forwardRef<
  React.ElementRef<typeof AlertDialogPrimitive.Description>,
  AlertDialogDescriptionProps
>(({ className, ...props }, ref) => (
  <AlertDialogPrimitive.Description
    ref={ref}
    className={cn(alertDialogDescriptionClasses, className)}
    {...props}
  />
));
AlertDialogDescription.displayName = AlertDialogPrimitive.Description.displayName;

/**
 * Radix owns these two elements, so they wear the button recipe rather than
 * rendering a `Button`. `useButtonProps` is the same resolution `Button` runs,
 * so they track `release-experimental-design-system-forms` instead of being
 * pinned to the stable recipe (DS-1575).
 *
 * Style them via `variant`; size is owned by the component and follows the
 * recipe default (`lg` stable, `md` experimental). A `buttonVariants(...)` string passed
 * through `className` lands after the recipe and re-pins the button to stable —
 * see `alert-dialog.types.ts`.
 */
const AlertDialogAction = React.forwardRef<
  React.ElementRef<typeof AlertDialogPrimitive.Action>,
  AlertDialogActionProps
>(({ className, variant, ...props }, ref) => {
  const buttonProps = useButtonProps({ variant });
  return (
    <AlertDialogPrimitive.Action
      ref={ref}
      {...buttonProps}
      className={cn(buttonProps.className, className)}
      {...props}
    />
  );
});
AlertDialogAction.displayName = AlertDialogPrimitive.Action.displayName;

const AlertDialogCancel = React.forwardRef<
  React.ElementRef<typeof AlertDialogPrimitive.Cancel>,
  AlertDialogCancelProps
>(({ className, variant = 'ghost', ...props }, ref) => {
  const buttonProps = useButtonProps({ variant });
  return (
    <AlertDialogPrimitive.Cancel
      ref={ref}
      {...buttonProps}
      className={cn(buttonProps.className, alertDialogCancelClasses, className)}
      {...props}
    />
  );
});
AlertDialogCancel.displayName = AlertDialogPrimitive.Cancel.displayName;

export {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  AlertDialogPortal,
  AlertDialogTitle,
  AlertDialogTrigger,
};
